/**
 * @module NotificationsPage
 * @package @geenius/notifications-react
 * @description Renders the full inbox page for the Tailwind React variant. The
 * page composes the shared inbox hook with grouped sections, status filters,
 * and type filters.
 */

import { FadeIn, motion } from '@geenius/motion/react'
import type {
  Notification,
  NotificationStatus,
} from '@geenius/notifications/shared'
import {
  buildNotificationsSeoMeta,
  getNotificationStatusLabel,
  getNotificationTypeLabel,
  groupNotificationsForDisplay,
  NOTIFICATION_TYPES,
  TYPE_CONFIG,
} from '@geenius/notifications/shared'
import { SEOHead } from '@geenius/seo/react'
import { type JSX, useMemo } from 'react'
import { EmptyNotifications } from '../components/EmptyNotifications'
import { NotificationItem } from '../components/NotificationItem'
import { useNotifications } from '../hooks/useNotifications'
import { useNotificationCopy } from '../lib/notificationCopy'
import {
  getNotificationAccentStyle,
  notificationToggleVariants,
  notificationTypeFilterVariants,
} from '../lib/notificationStyles'
import { cn } from '../lib/utils'

const NOTIFICATION_SKELETON_KEYS = [
  'notifications-page-skeleton-1',
  'notifications-page-skeleton-2',
  'notifications-page-skeleton-3',
  'notifications-page-skeleton-4',
  'notifications-page-skeleton-5',
  'notifications-page-skeleton-6',
]

const NOTIFICATION_STATUS_FILTERS: ReadonlyArray<NotificationStatus | 'all'> = [
  'all',
  'unread',
  'read',
]

/**
 * Props for the React notifications page.
 */
export interface NotificationsPageProps {
  notifications: Notification[] | undefined
  onMarkRead?: (id: string) => Promise<void>
  onMarkAllRead?: () => Promise<void>
  onDismiss?: (id: string) => Promise<void>
  onDelete?: (id: string) => Promise<void>
  className?: string
}

/**
 * Renders the full notifications inbox.
 *
 * @param props Page props including notification data and mutation handlers.
 * @returns The notifications page markup.
 */
export function NotificationsPage({
  notifications: rawNotifications,
  onMarkRead,
  onMarkAllRead,
  onDismiss,
  onDelete,
  className,
}: NotificationsPageProps): JSX.Element {
  const { plural, t } = useNotificationCopy()
  const {
    notifications,
    unreadCount,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    isLoading,
  } = useNotifications(rawNotifications, {
    markRead: onMarkRead ?? (async () => {}),
    markAllRead: onMarkAllRead ?? (async () => {}),
    dismiss: onDismiss ?? (async () => {}),
    deleteNotification: onDelete ?? (async () => {}),
  })

  const groupedSections = useMemo(
    () => Array.from(groupNotificationsForDisplay(notifications, t).entries()),
    [notifications, t],
  )

  const seoMeta = buildNotificationsSeoMeta({
    title: t('notifications.seo.notificationsTitle'),
    description: t('notifications.seo.notificationsDescription'),
    path: '/notifications',
    keywords: ['notifications', 'inbox', 'alerts', 'activity'],
  })

  if (isLoading) {
    return (
      <div className={cn('flex flex-col gap-3 p-6', className)}>
        {NOTIFICATION_SKELETON_KEYS.map((skeletonKey) => (
          <div
            key={skeletonKey}
            className="h-16 animate-pulse rounded-xl bg-[var(--gn-notifications-skeleton)]"
          />
        ))}
      </div>
    )
  }

  return (
    <>
      <SEOHead meta={seoMeta} />
      <section className={cn('mx-auto max-w-3xl space-y-6 p-6', className)}>
        <FadeIn className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('notifications.common.title')}
            </h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {plural('notifications.common.unreadSummary', unreadCount)}
            </p>
          </div>
          {unreadCount > 0 ? (
            <motion.button
              type="button"
              onClick={onMarkAllRead}
              whileHover={{ y: -1, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-xs font-semibold uppercase tracking-wide text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              {t('notifications.common.markAllRead')}
            </motion.button>
          ) : null}
        </FadeIn>

        <FadeIn className="flex flex-wrap gap-2">
          {NOTIFICATION_STATUS_FILTERS.map((status) => (
            <motion.button
              key={status}
              type="button"
              aria-pressed={statusFilter === status}
              onClick={() => setStatusFilter(status)}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.98 }}
              className={notificationToggleVariants({ active: statusFilter === status })}
            >
              {getNotificationStatusLabel(status, t)}
            </motion.button>
          ))}
        </FadeIn>

        <FadeIn className="flex flex-wrap gap-1.5">
          <motion.button
            type="button"
            aria-pressed={typeFilter === 'all'}
            onClick={() => setTypeFilter('all')}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
            className={notificationToggleVariants({ active: typeFilter === 'all' })}
          >
            {t('notifications.filters.typeAll')}
          </motion.button>
          {NOTIFICATION_TYPES.map((type) => (
            <motion.button
              key={type}
              type="button"
              aria-pressed={typeFilter === type}
              onClick={() => setTypeFilter(type)}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.98 }}
              style={getNotificationAccentStyle(type)}
              className={notificationTypeFilterVariants({ active: typeFilter === type })}
            >
              {TYPE_CONFIG[type].icon} {getNotificationTypeLabel(type, t)}
            </motion.button>
          ))}
        </FadeIn>

        {notifications.length === 0 ? (
          <EmptyNotifications />
        ) : (
          groupedSections.map(([label, items], _index) => (
            <FadeIn key={label}>
              <div className="space-y-2">
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  {label}
                </h2>
                <div className="overflow-hidden rounded-xl border border-border">
                  {items.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onRead={onMarkRead}
                      onDismiss={onDismiss}
                    />
                  ))}
                </div>
              </div>
            </FadeIn>
          ))
        )}
      </section>
    </>
  )
}
